import React, { useState, useEffect } from 'react';
import { Clock, User, FileText } from 'lucide-react';
import { editorAPI } from '@/lib/editor/api';

interface Version {
  _id: string;
  message: string;
  createdAt: string;
  createdBy: string;
  fileCount?: number;
  totalSize?: number;
}

interface VersionHistoryProps {
  projectId: string;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({ projectId }) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVersions();
  }, [projectId]);

  const loadVersions = async () => {
    try {
      const data = await editorAPI.getVersionHistory(projectId);
      setVersions(data);
    } catch (error) {
      console.error('Failed to load versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.round((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      'day'
    );
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Loading versions...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800">
      <div className="p-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Version History</h3>
      </div>

      <div className="p-3 space-y-2">
        {versions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No versions yet</p>
        ) : (
          versions.map((version) => (
            <div
              key={version._id}
              className="p-3 border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between mb-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {version.message}
                </p>
              </div>
              
              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {formatDate(version.createdAt)}
                </span>
                
                {version.fileCount !== undefined && (
                  <span className="flex items-center gap-1">
                    <FileText size={12} />
                    {version.fileCount} files
                  </span>
                )}
                
                {version.totalSize !== undefined && (
                  <span>{formatSize(version.totalSize)}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};